import { IComponentCallback, titleize } from "@cd2/tools"
import React, { useEffect } from "react"
import { useHistory } from "react-router-dom"
import { IChange, useEvents } from "../../Components/EventsList"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { PageWrapper } from "../../Components/PageWrapper"
import { supportTicketMutation } from "../../Components/SupportTicketForm"
import { icons } from "../../constants/icons"
import { useApi } from "../../hooks/useApi"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { buildRoute, eRoutes } from "../../routes"
import { IRecord } from "../../types/schema"
import { rmaStatusColorMap } from "./RmaStatusCard"
import { IRmaRecord, RmaRequestType, RmaStatus, rmaStatusMessages } from "./RmasIndexPage"
import { BranchRmaForm, IRmaNotificationStatus, useHandleSendRmaEmail } from "./BranchRmaForm"
import { isFakeID } from "./CustomerRmaForm"

export function RmaNewPage() {
  const { currentUser } = useGlobalState()
  const history = useHistory()
  const alerts = useSiteAlerts()
  const events = useEvents()
  const api = useApi()
  const [initialData, setInitialData] = React.useState<Partial<IRmaRecord> | null>(null)
  const handleSendRmaEmail = useHandleSendRmaEmail({ skippable: true })

  async function handleSubmit(data: IComponentCallback<typeof BranchRmaForm, "onSubmit">) {
    try {
      const saved = await handleRmaSubmit({ events, api, handleSendRmaEmail }, undefined, data)
      if (saved) {
        alerts.genericSuccess("RMA created successfully")
        history.push(buildRoute(eRoutes["/customer-support/rma/:id"], { id: saved.id }))
      } else {
        alerts.genericError("Failed to create RMA")
      }
    } catch (e) {
      alerts.genericError(e.message)
    }
  }

  useEffect(() => {
    if (!currentUser.branch?.id) {
      return
    }
    const initialData = {
      branch: currentUser.branch as any,
      customer: currentUser.customer,
      assignedTo: currentUser as any,
      status: RmaStatus.PENDING,
    }
    if (history.location?.state) {
      Object.assign(initialData, history.location.state)
    }
    console.log("INITDATA", initialData, history.location?.state)
    setInitialData(initialData)
  }, [currentUser.branch?.id])

  if (!initialData) {
    return null
  }

  return (
    <div>
      <PageWrapper title="New RMA" breadcrumbPath={["rma", { title: "New" }]} dontPadChildren>
        <BranchRmaForm onSubmit={handleSubmit} initialData={initialData} />
      </PageWrapper>
    </div>
  )
}

interface IQueryData {
  branch?: { set: string | null }
  items?: { create?: any; update?: any; destroy?: any }[]
  order?: { set: string | null }
  attachments?: { create?: any; update?: any; destroy?: any }[]
  customer?: { set: string | null }
  sub_customer?: { set: string | null }
  contact?: { set: string | null }
  assignedTo?: { set: string | null }
  supportTicket?: { set: string | null }
  internalRmaBranch?: { set: string | null }
  created_by?: { set: string | null }

  requestType: RmaRequestType
  creditNumber: string
  orderDate: Date
  returnReason: string
  additionalDetails: string
  technicalCaseNumber: string
  returnsNumber: string
  status: RmaStatus
  salesNumber: string
  identifier?: string
  closedReason?: string
  statusMessage?: string
  notes?: string
  show_notes_on_confirmation?: boolean
}

export async function handleRmaSubmit(
  context: {
    events: ReturnType<typeof useEvents>
    api: ReturnType<typeof useApi>
    handleSendRmaEmail?: (rmaId: string, initialStatus?: IRmaNotificationStatus) => Promise<void>
  },
  id: string | undefined,
  data: IComponentCallback<typeof BranchRmaForm, "onSubmit">,
  initialData?: Partial<IRmaRecord>,
) {
  const { events } = context
  const skeem = window.skeem

  async function resolveCreatedById(): Promise<string | null> {
    let created_by: { id: string } | undefined = data.created_by
    if (!created_by && data.contact?.id) {
      const contact = await skeem.fetch2<IRecord<"contacts", "user">>({
        first: {
          name: "contacts",
          filter: { matches: { id: data.contact.id } },
          attributes: ["user"],
        },
      })
      if (contact?.user) {
        created_by = contact.user
      }
    }
    if (!created_by) {
      const sessionUser = await skeem.fetch2<IRecord<"skeem_users">>({
        first: {
          name: "skeem_users",
          filter: { matches: { id: { session: true } } },
        },
      })
      if (sessionUser) {
        created_by = sessionUser
      }
    }
    return created_by?.id ?? null
  }

  let createdByAssociation: { set: string | null } | undefined
  if (!id) {
    createdByAssociation = { set: await resolveCreatedById() }
  } else if (!initialData?.created_by?.id) {
    const createdById = await resolveCreatedById()
    if (createdById) {
      createdByAssociation = { set: createdById }
    }
  }

  const queryData: IQueryData = {
    internalRmaBranch: { set: data.internalRmaBranch?.id ?? null },
    branch: { set: data.branch?.id ?? null },
    order: { set: data.order?.id ?? null },
    ...(createdByAssociation ? { created_by: createdByAssociation } : {}),
    attachments: [] as any[], // TODO

    // stuff that customer should not be able to change
    contact: { set: data.contact?.id ?? null },

    customer: { set: data.customer?.id ?? null },
    sub_customer: { set: data.sub_customer?.id ?? null },

    assignedTo: { set: data.assignedTo?.id ?? null },
    supportTicket: { set: data.supportTicket?.id ?? null },

    statusMessage: data.statusMessage ?? "",
    closedReason: data.closedReason ?? "",
    orderDate: data.orderDate ?? undefined,
    returnReason: data.returnReason ?? undefined,
    creditNumber: data.creditNumber,
    additionalDetails: data.additionalDetails,
    returnsNumber: data.returnsNumber,
    requestType: data.requestType,
    status: data.status?.value ?? "",
    salesNumber: data.salesNumber,
  }
  if (data.notes !== undefined) {
    queryData.notes = data.notes
  }
  if (data.show_notes_on_confirmation !== undefined) {
    queryData.show_notes_on_confirmation = data.show_notes_on_confirmation
  }

  const itemChanges =
    data.items
      ?.map(item => {
        if (item._delete) {
          if (isFakeID(item.id)) {
            return null
          }
          return { destroy: item.id }
        }
        if (isFakeID(item.id)) {
          return {
            create: {
              quantity: item.quantity ?? 1,
              serial_number: item.serial_number,
              tech_case_number: item.tech_case_number,
              item_type: item.item_type ?? "product",
              comment: item.comment,
              product: { set: item.product?.id ?? null },
              order: { set: item.order?.id ?? null },
            },
          }
        }
        return {
          update: {
            id: item.id,
            quantity: item.quantity ?? 1,
            serial_number: item.serial_number,
            tech_case_number: item.tech_case_number,
            item_type: item.item_type ?? "product",
            comment: item.comment,
            product: { set: item.product?.id ?? null },
            order: { set: item.order?.id ?? null },
          },
        }
      })
      .filter(Boolean) ?? []
  queryData.items = itemChanges

  const attachmentChanges = (data.attachments?.map(attachment => {
    const queryData = {
      fileName: attachment.fileName,
      fileboyId: attachment.fileboyId,
    }
    if (isFakeID(attachment.id)) {
      return { create: queryData }
    } else {
      return { update: { id: attachment.id, ...queryData } }
    }
  }) ?? []) as any[]
  const removedAttachments = initialData?.attachments?.filter(
    a => !data.attachments?.some(b => a.id === b.id),
  )
  attachmentChanges.push(...(removedAttachments?.map(x => ({ destroy: x.id })) ?? []))
  queryData.attachments = attachmentChanges

  const eventsToTriggerEmail = [
    RmaStatus.REJECTED,
    RmaStatus.AWAITING_GOODS_RECEIPT,
    RmaStatus.RAISED,
  ]

  if (id) {
    const record = await skeem.mutate({ rmas: { update: { ...queryData, id } } })
    const updateChanges = diffRecord(data, initialData)
    if (updateChanges.length) {
      await events.updateRecord("rmas", record.id, diffRecord(data, initialData))
    }
    await buildEvents(events, record.id, queryData, initialData)
    console.log(queryData.status, initialData?.status)
    if (
      eventsToTriggerEmail.includes(queryData.status) &&
      queryData.status !== initialData?.status
    ) {
      await context.handleSendRmaEmail?.(
        record.id,
        queryData.status === RmaStatus.REJECTED ? "rejected" : "approved",
      )
    }
    return record
  } else {
    const { identifier } = await context.api.nextIdentifier("rma")
    queryData.identifier = identifier
    const record = await skeem.mutate({ rmas: { create: queryData } })
    await events.createRecord("rmas", record.id)
    await buildEvents(events, record.id, queryData, initialData)
    if (eventsToTriggerEmail.includes(queryData.status)) {
      await context.handleSendRmaEmail?.(
        record.id,
        queryData.status === RmaStatus.REJECTED ? "rejected" : "approved",
      )
    }
    return record
  }
}

function valueFormatParser(value: any) {
  if (value instanceof Date) {
    return value.toISOString()
  }
  return value
}
function diffRecord(
  newData: IComponentCallback<typeof BranchRmaForm, "onSubmit">,
  oldData?: Partial<IRmaRecord>,
) {
  const attrs: (keyof typeof newData)[] = [
    "additionalDetails",
    "closedReason",
    "orderDate",
    "requestType",
    "creditNumber",
    "returnReason",
    "returnsNumber",
    "salesNumber",
    "statusMessage",
    "technicalCaseNumber",
  ]
  const changes: IChange[] = []
  for (const attr of attrs) {
    const prev = valueFormatParser(oldData?.[attr])
    const curr = valueFormatParser(newData[attr])
    if (curr !== prev) {
      changes.push({
        name: titleize(attr),
        type: "update",
        current: curr,
        previous: prev,
      })
    }
  }
  return changes
}

export async function handleRMASupportTicket(
  context: {
    skeem: typeof window.skeem
    events: ReturnType<typeof useEvents>
    api: ReturnType<typeof useApi>
  },
  data: IComponentCallback<typeof BranchRmaForm, "onSubmit">,
) {
  const { skeem, events, api } = context

  const type = await skeem.fetch2<IRecord<"support_ticket_types">>({
    first: { name: "support_ticket_types", matches: { defaultForRma: true } },
  })
  const source = await skeem.fetch2<IRecord<"support_ticket_sources">>({
    first: { name: "support_ticket_sources", matches: { isDefault: true } },
  })
  const creatingUser = await skeem.fetch2<IRecord<"skeem_users", "defaultSupportTicketGroup">>({
    first: {
      name: "skeem_users",
      attributes: ["defaultSupportTicketGroup"],
      filter: { matches: { id: { session: true } } },
    },
  })
  const group = await skeem.fetch2<IRecord<"support_ticket_groups", "name">>({
    first: {
      name: "support_ticket_groups",
      attributes: ["name", "isTechnical"],
      filter: { matches: { isDefault: true } },
    },
  })
  if (type) {
    const ticket = await supportTicketMutation(
      {
        assigned_to_user: data.assignedTo?.id ?? null,
        branch: data.branch?.id ?? null,
        assigned_to_group: creatingUser?.defaultSupportTicketGroup?.id ?? group?.id,
        contact: data.contact?.id ?? null,
        customer: data.customer?.id ?? null,
        details: data.additionalDetails,
        files:
          data.attachments?.map(x => ({
            key: x.id,
            id: isFakeID(x.id) ? undefined : x.id,
            _delete: false,
            fileboyId: x.fileboyId,
            fileName: x.fileName,
          })) ?? [],
        order: data.order?.id ?? null,
        order_item: null,
        priority: "medium",
        product_master: null,
        // product_master: data.product?.product_master?.id ?? null, // TODO
        resolution: "",
        serial_number: null,
        source: source?.id,
        status: "open",
        title: `RMA for ${data.customer?.name}`,
        type: type.id,
      },
      { skeem, events, api },
      undefined,
      undefined,
    )
    return { record: ticket, error: null }
  }
  return { record: null, error: "Missing required support ticket type" }
}

async function buildEvents(
  events: ReturnType<typeof useEvents>,
  id: string,
  queryData: IQueryData,
  initialData?: Partial<IRmaRecord>,
) {
  if (queryData.branch?.set !== initialData?.branch?.id && queryData.branch?.set) {
    await events.associateWithInverse(
      "rmas",
      id,
      {
        association: "branches",
        recordId: queryData.branch.set,
        nameAttr: "name",
      },
      "identifier",
    )
  }
  if (queryData.assignedTo?.set !== initialData?.assignedTo?.id && queryData.assignedTo?.set) {
    await events.associateWithInverse(
      "rmas",
      id,
      {
        association: "skeem_users",
        recordId: queryData.assignedTo.set,
        nameAttr: "name",
      },
      "identifier",
    )
  }
  if (queryData.customer?.set !== initialData?.customer?.id && queryData.customer?.set) {
    await events.associateWithInverse(
      "rmas",
      id,
      {
        association: "customers",
        recordId: queryData.customer.set,
        nameAttr: "name",
      },
      "identifier",
    )
  }
  if (queryData.contact?.set !== initialData?.contact?.id && queryData.contact?.set) {
    await events.associateWithInverse(
      "rmas",
      id,
      {
        association: "contacts",
        recordId: queryData.contact.set,
        nameAttr: "name",
      },
      "identifier",
    )
  }
  if (queryData.order?.set !== initialData?.order?.id && queryData.order?.set) {
    await events.associateWithInverse(
      "rmas",
      id,
      {
        association: "orders",
        recordId: queryData.order.set,
        nameAttr: "identifier",
      },
      "identifier",
    )
  }
  if (
    queryData.supportTicket?.set !== initialData?.supportTicket?.id &&
    queryData.supportTicket?.set
  ) {
    await events.associateWithInverse(
      "rmas",
      id,
      {
        association: "support_tickets",
        recordId: queryData.supportTicket.set,
        nameAttr: "identifier",
      },
      "identifier",
    )
  }
  if (queryData.status !== initialData?.status) {
    events.statusUpdate("rmas", id, queryData.status, {
      color: rmaStatusColorMap[queryData.status]?.bg,
      icon: queryData.status === RmaStatus.REJECTED ? icons.cross : icons.check,
      friendlyStatus: rmaStatusMessages[queryData.status] ?? "",
      previousStatus:
        rmaStatusMessages[initialData?.status as keyof typeof rmaStatusMessages] ?? "",
    })
  }
}

